import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Icon, itemVisible } from '../../data/map';
import { accessMap } from '../../data/accessMap';



const ORANGE = '#f97316';
const PURPLE = '#a855f7';
const DEFAULT_ACCENT = '#22d3ee';

const detectInitialMode = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'desktop';
  return window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
};

const suffixForMode = (mode) => (mode === 'desktop' ? 'd' : 'm');

const baseId = (id) => {
  if (!id) return id;
  return id.endsWith('d') || id.endsWith('m') ? id.slice(0, -1) : id;
};

/* ──────────── TAB PILL (leaf) ──────────── */
const TabPill = ({ label }) => (
  <span className="pm-tab-pill">{label}</span>
);

/* ──────────── PAGE NODE (middle tier — controlled) ──────────── */
const PageNode = ({ page, isOpen, onToggle, onHeightChange }) => {
  const contentRef = useRef(null);
  const [h, setH] = useState(0);

  useEffect(() => {
    if (!contentRef.current) return;
    const newH = isOpen ? contentRef.current.scrollHeight : 0;
    setH(newH);
    if (onHeightChange) {
      requestAnimationFrame(onHeightChange);
      const t = setTimeout(onHeightChange, 270);
      return () => clearTimeout(t);
    }
  }, [isOpen, onHeightChange]);

  return (
    <div className="pm-page-node">
      <div
        className="pm-page-row"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className={`pm-chev ${isOpen ? 'open' : ''}`} aria-hidden="true">
          <Icon name="chevronRight" color="rgba(168,85,247,0.65)" fallback="doc" />
        </span>
        <span className="pm-page-icon">
          <Icon name={page.icon} color={PURPLE} fallback="doc" />
        </span>
        <span className="pm-page-label">{page.label}</span>
        <span className="pm-count-badge">{page.tabs.length}</span>
      </div>

      <div
        className={`pm-page-tabs-wrap ${isOpen ? 'is-open' : 'is-closed'}`}
        style={{ height: `${h}px` }}
      >
        <div ref={contentRef} className="pm-page-tabs">
          {page.tabs.map((t, i) => (
            <TabPill key={`${page.id}-tab-${i}`} label={t} />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ──────────── ENTRY NODE (top tier — controlled) ──────────── */
const EntryNode = ({ entry, isOpen, onToggle, onHeightChange }) => {
  const contentRef = useRef(null);
  const [h, setH] = useState(0);
  const [openPageId, setOpenPageId] = useState(null);

  const remeasure = useCallback(() => {
    if (!contentRef.current) return;
    setH(contentRef.current.scrollHeight);
    if (onHeightChange) onHeightChange();
  }, [onHeightChange]);

  useEffect(() => {
    if (isOpen) {
      remeasure();
    } else {
      setH(0);
      setOpenPageId(null);
      if (onHeightChange) onHeightChange();
    }
  }, [isOpen, remeasure, onHeightChange]);

  useEffect(() => {
    if (!isOpen || !contentRef.current) return;
    const obs = new MutationObserver(remeasure);
    obs.observe(contentRef.current, { subtree: true, attributes: true, childList: true });
    return () => obs.disconnect();
  }, [isOpen, remeasure]);

  const handlePageToggle = useCallback((pageId) => {
    setOpenPageId((current) => (current === pageId ? null : pageId));
  }, []);

  return (
    <div className={`pm-entry-node ${isOpen ? 'pm-entry-open' : ''}`}>
      <div
        className="pm-entry-row"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className={`pm-chev ${isOpen ? 'open' : ''}`} aria-hidden="true">
          <Icon name="chevronRight" color="rgba(249,115,22,0.85)" fallback="doc" />
        </span>
        <span className="pm-entry-icon">
          <Icon name={entry.icon} color={ORANGE} fallback="doc" />
        </span>
        <span className="pm-entry-label">{entry.label}</span>
        <span className="pm-count-badge">{entry.pages.length}</span>
      </div>

      <div
        className={`pm-entry-children-wrap ${isOpen ? 'is-open' : 'is-closed'}`}
        style={{ height: `${h}px` }}
      >
        <div ref={contentRef} className="pm-entry-children">
          {entry.pages.map((p) => (
            <PageNode
              key={`${entry.id}-${p.id}`}
              page={p}
              isOpen={openPageId === p.id}
              onToggle={() => handlePageToggle(p.id)}
              onHeightChange={remeasure}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ──────────── MAIN ──────────── */
// `user` gates entries that carry `requires` (e.g. Work Availability, requires: 'casual')
// via itemVisible — the same rule the ProfileHome accordion applies to navSections items.
// `userJob` is optional; it falls back to user.userJob for the badge.
const ProfileMap = ({ visibleSections = [], user = null, userJob, accent = DEFAULT_ACCENT }) => {
  const MAP_ACCENT = accent || DEFAULT_ACCENT;
  const jobLabel = userJob || user?.userJob || '';
  const [mode, setMode] = useState(detectInitialMode);
  const [openKey, setOpenKey] = useState(null);

  // Outer shell accordion — starts CLOSED, matches ProfileHome accordion
  const [shellOpen, setShellOpen] = useState(false);
  const shellContentRef = useRef(null);
  const [shellHeight, setShellHeight] = useState(0);

  const roleBases = useMemo(() => {
    const set = new Set();
    visibleSections.forEach((s) => set.add(baseId(s.id)));
    return Array.from(set);
  }, [visibleSections]);

  const trees = useMemo(() => {
    const suffix = suffixForMode(mode);
    const out = [];
    roleBases.forEach((base) => {
      const id = `${base}${suffix}`;
      const tree = accessMap[id];
      if (!tree) return;
      const matchingSection =
        visibleSections.find((s) => s.id === id) ||
        visibleSections.find((s) => baseId(s.id) === base);
      const entries = (tree.entries || []).filter((entry) => itemVisible(entry, user));
      if (!entries.length) return;
      out.push({
        id,
        label: matchingSection?.label || base,
        accentColor: matchingSection?.accentColor || MAP_ACCENT,
        entries,
      });
    });
    return out;
  }, [roleBases, visibleSections, mode, MAP_ACCENT, user]);

  // Total entry count across all trees — what we show as "X items" in the shell header
  const totalEntries = useMemo(
    () => trees.reduce((sum, t) => sum + t.entries.length, 0),
    [trees]
  );

  // Reset open state when mode changes
  useEffect(() => {
    setOpenKey(null);
  }, [mode]);

  // Remeasure shell height when its contents change (inner expansions, mode switch)
  const remeasureShell = useCallback(() => {
    if (!shellContentRef.current) return;
    if (shellOpen) {
      setShellHeight(shellContentRef.current.scrollHeight);
    } else {
      setShellHeight(0);
    }
  }, [shellOpen]);

  useEffect(() => {
    if (shellOpen && shellContentRef.current) {
      remeasureShell();
      const obs = new MutationObserver(remeasureShell);
      obs.observe(shellContentRef.current, {
        subtree: true,
        attributes: true,
        childList: true,
      });
      const t = setTimeout(remeasureShell, 350);
      return () => {
        obs.disconnect();
        clearTimeout(t);
      };
    } else {
      setShellHeight(0);
    }
  }, [shellOpen, remeasureShell, mode, openKey, totalEntries]);

  const handleToggle = useCallback((key) => {
    setOpenKey((current) => (current === key ? null : key));
  }, []);

  const handleCollapseAll = (e) => {
    if (e) e.stopPropagation();
    setOpenKey(null);
  };

  const handleShellToggle = () => {
    setShellOpen((prev) => !prev);
  };

  const handleModeClick = (e, m) => {
    e.stopPropagation();
    setMode(m);
  };

  const renderInner = () => {
    if (!trees.length) {
      return (
        <div className="pm-tree">
          <div style={{ padding: 16, color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
            No {mode} tree configured
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="pm-tree">
          {trees.map((tree) => (
            <div key={`${tree.id}-${mode}`} className="pm-tree-section">
              {trees.length > 1 && (
                <div className="pm-section-label" style={{ color: tree.accentColor }}>
                  {tree.label}
                </div>
              )}
              {tree.entries.map((entry) => {
                const key = `${tree.id}::${entry.id}`;
                return (
                  <EntryNode
                    key={`${key}-${mode}`}
                    entry={entry}
                    isOpen={openKey === key}
                    onToggle={() => handleToggle(key)}
                    onHeightChange={remeasureShell}
                  />
                );
              })}
            </div>
          ))}
        </div>

        <div className="pm-footer">
          <button
            type="button"
            className="pm-mini-btn"
            onClick={handleCollapseAll}
            disabled={!openKey}
          >
            Collapse
          </button>
        </div>
      </>
    );
  };

  return (
    <div
      className={`profile-accordion ${shellOpen ? 'open' : ''}`}
      style={{ '--accent': MAP_ACCENT }}
    >
      <div className="profile-accordion-header" onClick={handleShellToggle}>
        <div className="profile-accordion-left">
          <div
            className="profile-accordion-icon"
            style={{
              background: `${MAP_ACCENT}15`,
              borderColor: `${MAP_ACCENT}30`,
            }}
          >
            <Icon name="filter" color={MAP_ACCENT} fallback="doc" />
          </div>
          <div className="profile-accordion-info">
            <span className="profile-accordion-label">Site Map</span>
            <span className="profile-accordion-count">
              {totalEntries} {totalEntries === 1 ? 'item' : 'items'}
            </span>
            {jobLabel && (
              <span
                style={{
                  fontSize: 11,
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginTop: 2,
                }}
              >
                {jobLabel}
              </span>
            )}
          </div>
        </div>

        <div
          className="pm-mode-toggle"
          role="tablist"
          aria-label="Viewport mode"
          onClick={(e) => e.stopPropagation()}
          style={{ marginRight: 12 }}
        >
          <button
            type="button"
            className={`pm-mode-btn ${mode === 'desktop' ? 'active' : ''}`}
            onClick={(e) => handleModeClick(e, 'desktop')}
            role="tab"
            aria-selected={mode === 'desktop'}
          >
            Desktop
          </button>
          <button
            type="button"
            className={`pm-mode-btn ${mode === 'mobile' ? 'active' : ''}`}
            onClick={(e) => handleModeClick(e, 'mobile')}
            role="tab"
            aria-selected={mode === 'mobile'}
          >
            Mobile
          </button>
        </div>

        <div className={`profile-accordion-chevron ${shellOpen ? 'rotated' : ''}`}>
          <Icon
            name="chevronDown"
            color={shellOpen ? MAP_ACCENT : 'rgba(255,255,255,0.4)'}
          />
        </div>
      </div>

      <div
        className="profile-accordion-content"
        style={{ height: `${shellHeight}px` }}
      >
        <div ref={shellContentRef} className="profile-accordion-inner">
          <div
            className="pm-container"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              boxShadow: 'none',
            }}
          >
            {renderInner()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileMap;